import React, { useEffect } from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';

import { Greet } from '~/types/models';
import { ViewWebsiteButton } from '../../../Components/RecorderDrawer/Components/ViewWebsiteButton';
import { GREET_RECORD_NOW } from '~/generated/AnalyticsTypes';
import { trackGreetEvent } from '~/App/Services/greet';
import { useQueryString } from '~/react-hooks/use-query-string';
import { openGreetRecorderDrawer } from '../Components/GreetRecorderDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faRobot } from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from '@vimily/bonjoro-ui-components-v2/Tooltip';
import ProfileImage from '~/App/Components/ProfileImage';
import ProfileHeader from '~/App/Modules/TodoList/Components/GreetDetails/ProfileHeader';
import { formatSnakeCaseToTitle } from '~/App/Components/Attributes/Formatter';
import { isValidUrl } from '~/Share/Utils';
import { useTeam } from '../../Settings/NewTeam/Hooks/useTeam';
import { useAiEnrichment } from '~/react-hooks/use-ai-enrichment';

export type GreetRecordBodyContainerProps = {
  greet: Greet;
};

type ProfileInfoItemProps = {
  label: string;
  value: React.ReactNode;
};

const ProfileInfoItem = ({ label, value }: ProfileInfoItemProps) => {
  return (
    <div className='flex flex-wrap'>
      <span className='w-[140px] flex'>{label}:</span>
      <span className='min-w-[190px] text-neutral-500'>{value}</span>
    </div>
  );
};

const handleRecordClick = (greet: Greet) => {
  trackGreetEvent(GREET_RECORD_NOW, greet);
  openGreetRecorderDrawer(greet);
};

export default function GreetRecordContainer({
  greet,
}: GreetRecordBodyContainerProps) {
  const { profiles } = greet;
  const { openRecorder } = useQueryString<{ openRecorder: boolean }>();

  const profile = profiles?.[0];
  const isRollUp = profiles && profiles.length > 1;
  const assignee = greet.assignee;
  const isAiEnrichmentEnabled = useAiEnrichment();

  useEffect(() => {
    if (openRecorder) {
      openGreetRecorderDrawer(greet);
    }
  }, []);

  if (!profile) {
    return null;
  }

  return (
    <div className='border border-solid v2-border-neutral-300 w-full rounded px-[20px] py-[15px] overflow-y-auto'>
      <div>
        {!isRollUp && (
          <div className='mb-[15px]'>
            <div className='flex items-center mb-3'>
              <ProfileImage profile={profile} size={50} />
              <ProfileHeader
                className='flex items-center justify-center'
                greet={greet}
              />
            </div>
            <div className='flex flex-row flex-wrap gap-x-3'>
              <span className='text-neutral-900'>{profile.email}</span>
              <ViewWebsiteButton email={profile.email} />
            </div>
          </div>
        )}
      </div>
      <Button
        className='text-[21px] text-neutral-900 font-bold justify-center w-full hidden md:!flex border-solid mb-4'
        variant={ButtonVariant.Primary}
        onClick={() => handleRecordClick(greet)}
      >
        Record
      </Button>
      <div className='flex flex-col gap-y-[15px] px-4'>
        {isAiEnrichmentEnabled && (<div className='flex flex-col gap-y-2'>
          {profile.summary && (
            <>
              <span className='text-base'>{profile.summary}</span>
              <div className='flex flex-col gap-y-0.5'>
                {profile.social_links?.map((link) => {
                  if (!isValidUrl(link.url)) return null;

                  return (
                    <div className='flex gap-x-4' key={link.platform}>
                      <FontAwesomeIcon
                        icon={faLink}
                        className='w-4 h-4 text-neutral-500'
                      />
                      <span className='text-neutral-500'>
                        {formatSnakeCaseToTitle(link.platform)}:
                      </span>
                      <a href={link.url} target='_blank' rel='noreferrer'>
                        Link
                      </a>
                    </div>
                  );
                })}
              </div>
              <div className='flex flex-row gap-x-4 items-center text-blue-700 font-bold cursor-pointer'>
                <FontAwesomeIcon icon={faRobot} className='w-4 h-4' />
                <Tooltip
                  maxWidth={300}
                  maxHeight={200}
                  bubbleClassName='line-clamp-none w-full h-full p-2.5'
                  content={
                    <div className='text-sm'>
                      We pull this info from public sources to help you
                      personalise your message. It&apos;s generally pretty
                      accurate, but image matches aren&apos;t always 100%.
                      <br />
                      Don&apos;t worry, the customer never sees this. You can also
                      check their socials or website above for more context.
                    </div>
                  }
                >
                  <span>Where do we get this information?</span>
                </Tooltip>
                </div>
              </>
            )}
          </div>
        )}
        <div className='flex flex-col gap-y-2 border-0 border-t border-solid border-neutral-700 pt-2'>
          {greet.event?.message && (
            <ProfileInfoItem
              label='Task description'
              value={greet.event.message}
            />
          )}

          <div className='flex flex-wrap'>
            <span className='w-[140px] flex'>Assigned to:</span>
            <span className='min-w-[190px] flex gap-x-5'>
              <img
                src={assignee.image}
                width={30}
                height={30}
                className='rounded-full'
              />
              <span className='font-bold'>{assignee.first_name}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
